/**
 * Veri yaşam döngüsü sözleşmesi (M6 WP 6.4; K-M6-40; migration 0085–0087). Belge: docs/spec/castinflow/data-lifecycle.md.
 *
 * Bu sözleşme TEKNİK YETENEKLERİ tanımlar: veri öznesi dışa aktarma ve silme/anonimleştirme, saklama süreleri, hesap kapatma,
 * denetim izi görüntüleme ve süreli destek erişimi. Hukuki uygunluk değerlendirmesi içermez; o değerlendirme çalışma alanı
 * sahibinindir.
 */
import { z } from 'zod';
import { assistantActorSchema } from './assistant-name.js';
// ---------------------------------------------------------------------------------------------------------------------
// Saklama süreleri
// ---------------------------------------------------------------------------------------------------------------------
/** Çalışma alanının değiştirebildiği süreler: varsayılan + alt/üst sınır */
export const TENANT_RETENTION = {
    /** Silinen (çöp kutusundaki) kaydın kalıcı temizlenmesine kadar geçen gün */
    deletedRecordDays: { default: 30, min: 7, max: 365 },
    /** Denetim izinin saklandığı ay (plan §8: 24 ay) */
    auditMonths: { default: 24, min: 12, max: 120 },
};
/**
 * Platform düzeyi (yapılandırmayla; çalışma alanı değiştiremez) işletim verisi süreleri — gün. Alt sınırlar veritabanındaki
 * taban sürelerle aynıdır (`lifecycle_retention_purge`, migration 0085): yapılandırma tabanın altına inemez.
 */
export const PLATFORM_RETENTION = {
    /** Sonuçlanmış iş kayıtları (tekillik defteri): yinelenen teslimin ayırt edilebildiği süre */
    jobResultsDays: { default: 180, min: 30, max: 730 },
    /** Uygulama içi bildirimler */
    notificationsDays: { default: 180, min: 30, max: 730 },
    /** Dağıtılmış olay kuyruğu satırları (bütün abonelerin işlediği olaylar) */
    domainEventsDays: { default: 90, min: 7, max: 365 },
    /** Kapanmış (iptal edilmiş ya da süresi dolmuş) oturumlar ve kullanılmış/süresi dolmuş belirteçler */
    sessionsDays: { default: 30, min: 7, max: 180 },
};
export const retentionSettingsSchema = z.object({
    deletedRecordDays: z.number().int(),
    /** `true`: çalışma alanı özel değer vermemiş, platform varsayılanı geçerli */
    deletedRecordDaysIsDefault: z.boolean(),
    auditMonths: z.number().int(),
    auditMonthsIsDefault: z.boolean(),
    platform: z.object({
        jobResultsDays: z.number().int(),
        notificationsDays: z.number().int(),
        domainEventsDays: z.number().int(),
        sessionsDays: z.number().int(),
    }),
});
/** `null` = platform varsayılanına dön */
export const retentionSettingsPatchSchema = z
    .object({
    deletedRecordDays: z
        .number()
        .int()
        .min(TENANT_RETENTION.deletedRecordDays.min)
        .max(TENANT_RETENTION.deletedRecordDays.max)
        .nullable()
        .optional(),
    auditMonths: z
        .number()
        .int()
        .min(TENANT_RETENTION.auditMonths.min)
        .max(TENANT_RETENTION.auditMonths.max)
        .nullable()
        .optional(),
})
    .strict()
    .refine((v) => v.deletedRecordDays !== undefined || v.auditMonths !== undefined, {
    message: 'En az bir alan gerekli',
});
// ---------------------------------------------------------------------------------------------------------------------
// Veri öznesi talepleri (kişi / aday)
// ---------------------------------------------------------------------------------------------------------------------
export const SUBJECT_RECORD_TYPES = ['contact', 'lead'];
export const SUBJECT_RECORD_TYPE_LABELS = {
    contact: 'Kişi',
    lead: 'Aday',
};
export const SUBJECT_REQUEST_KINDS = ['export', 'erase'];
export const SUBJECT_REQUEST_STATUSES = [
    'queued',
    'running',
    'ready',
    'confirmed',
    'completed',
    'failed',
    'expired',
    'cancelled',
];
export const ERASE_MODES = ['delete', 'anonymize'];
export const ERASE_MODE_LABELS = {
    delete: 'Kalıcı sil',
    anonymize: 'Anonimleştir',
};
/**
 * Talep durum makinesi. Dışa aktarma: queued → running → ready → expired. Silme: queued → running → ready (ön izleme hazır)
 * → confirmed → running → completed. Her ara durumdan `failed`; onaylanmamış ön izleme `cancelled` ya da `expired` olur.
 */
const SUBJECT_TRANSITIONS = {
    export: {
        queued: ['running', 'failed'],
        running: ['ready', 'failed'],
        ready: ['expired'],
        confirmed: [],
        completed: [],
        failed: [],
        expired: [],
        cancelled: [],
    },
    erase: {
        queued: ['running', 'failed', 'cancelled'],
        running: ['ready', 'completed', 'failed'],
        ready: ['confirmed', 'cancelled', 'expired'],
        confirmed: ['running', 'failed'],
        completed: [],
        failed: [],
        expired: [],
        cancelled: [],
    },
};
export function canSubjectRequestTransition(kind, from, to) {
    return SUBJECT_TRANSITIONS[kind][from].includes(to);
}
export function isSubjectRequestTerminal(kind, status) {
    return SUBJECT_TRANSITIONS[kind][status].length === 0;
}
const countMapSchema = z.record(z.string(), z.number().int().nonnegative());
/**
 * Silme ön izlemesi / sonucu. Anahtarlar tablo adlarıdır (`LIFECYCLE_ROW_LABELS` ile etiketlenir). Ön izleme, gerçek
 * yürütmeyle AYNI veritabanı kodundan üretilir (iş bir alt işlemde yapılıp geri alınır); bu yüzden veri değişmediyse ön izleme
 * sayıları yürütme sonucuna eşittir.
 */
export const eraseOutcomeSchema = z.object({
    status: z.enum(['ok', 'referenced', 'not_found', 'unsupported', 'not_eligible']),
    mode: z.enum(ERASE_MODES).optional(),
    recordType: z.string().optional(),
    /** Kalıcı silinen satırlar */
    deleted: countMapSchema.prefault({}),
    /** Kişisel alanları temizlenen, satırı korunan kayıtlar */
    anonymized: countMapSchema.prefault({}),
    /** Korunan satırlar (gerekçe: `RETAINED_REASONS`) */
    retained: countMapSchema.prefault({}),
    /** Nesne deposundan silinecek dosya sayısı */
    objects: z.number().int().nonnegative().default(0),
    /** Kuyruktan kaldırılan bekleyen iş sayısı */
    queueJobs: z.number().int().nonnegative().default(0),
    /** `referenced`: kalıcı silmeyi engelleyen başvurular (`tablo.sütun` → sayı) */
    references: countMapSchema.optional(),
});
/** İki sonucun sayıları aynı mı (ön izleme ↔ yürütme karşılaştırması) */
export function sameEraseCounts(a, b) {
    const eq = (x, y) => {
        const kx = Object.keys(x).sort();
        const ky = Object.keys(y).sort();
        return kx.length === ky.length && kx.every((k, i) => k === ky[i] && x[k] === y[k]);
    };
    return (a.status === b.status &&
        a.mode === b.mode &&
        eq(a.deleted, b.deleted) &&
        eq(a.anonymized, b.anonymized) &&
        eq(a.retained, b.retained) &&
        a.objects === b.objects &&
        a.queueJobs === b.queueJobs);
}
/** Tablo anahtarı → arayüz etiketi. Listede olmayan anahtar "Diğer ilişkili satırlar" altında toplanır. */
export const LIFECYCLE_ROW_LABELS = {
    contacts: 'Kişi kaydı',
    leads: 'Aday kaydı',
    contact_infos: 'İletişim bilgisi',
    special_days: 'Özel gün',
    attachments: 'Dosya eki',
    inbound_attachments: 'Gelen e-posta eki',
    feed_items: 'Not ve akış kaydı',
    reminders: 'Hatırlatıcı',
    notifications: 'Bildirim',
    record_tags: 'Etiket bağı',
    record_favorites: 'Favori',
    record_acl: 'Paylaşım',
    activities: 'Aktivite',
    activity_links: 'Aktivite bağı',
    email_sends: 'E-posta gönderim kaydı',
    imap_messages: 'Gelen e-posta',
    lead_form_submissions: 'Web formu gönderim kaydı',
    lead_sequence_enrollments: 'Sekans kaydı',
    form_document_links: 'Form belgesi bağı',
    tickets: 'Destek talebi',
    webhook_deliveries: 'Web kancası teslim gövdesi',
    automation_runs: 'Otomasyon çalışması',
    domain_events: 'Olay kuyruğu satırı',
    import_rows: 'İçe aktarma ara satırı',
    search_documents: 'Arama indeksi satırı',
    search_terms: 'Arama indeksi satırı',
    search_records: 'Arama indeksi satırı',
    audit_events: 'Denetim izi',
    sales_documents: 'Satış belgesi',
    contracts: 'Sözleşme',
    opportunities: 'Fırsat',
};
export const LIFECYCLE_OTHER_LABEL = 'Diğer ilişkili satırlar';
/** Korunan satırların gerekçesi (ön izlemede ve sonuçta gösterilir) */
export const RETAINED_REASONS = {
    sales_documents: 'Belge, kilitli anlık görüntüsü ve çıktısı değiştirilmez: içerik özeti ve onay zinciri ilgili kişi kimliğine bağlıdır.',
    contracts: 'Sözleşme ve kilitli revizyonları değiştirilmez (belge bütünlüğü).',
    opportunities: 'Fırsat kaydı korunur; ilgili kişi bağı anonim kayda işaret eder.',
    activities: 'Başka kayıtlara da bağlı aktivite korunur; yalnız bu kayıtla bağı kaldırılır. Serbest metin gözden geçirilmelidir.',
    audit_events: 'Denetim izi satırı kalır (kim, ne zaman, hangi eylem); içerik alanı temizlenir, kayıt kimliği kalır.',
};
export function lifecycleRowLabel(key) {
    return LIFECYCLE_ROW_LABELS[key] ?? LIFECYCLE_OTHER_LABEL;
}
/** Sayı haritasını etikete göre toplar (aynı etiketi taşıyan tablolar tek satır olur), etikete göre sıralı döner */
export function summarizeCounts(counts) {
    const by = new Map();
    for (const [key, n] of Object.entries(counts)) {
        if (n <= 0)
            continue;
        const label = lifecycleRowLabel(key);
        const cur = by.get(label) ?? { count: 0, keys: [] };
        cur.count += n;
        cur.keys.push(key);
        by.set(label, cur);
    }
    return [...by.entries()]
        .map(([label, v]) => ({ label, count: v.count, keys: v.keys.sort() }))
        .sort((a, b) => a.label.localeCompare(b.label, 'tr'));
}
const userRefSchema = z.object({ id: z.guid(), name: z.string() }).nullable();
export const subjectRequestSchema = z.object({
    id: z.guid(),
    kind: z.enum(SUBJECT_REQUEST_KINDS),
    recordType: z.enum(SUBJECT_RECORD_TYPES),
    recordId: z.guid(),
    recordNo: z.number().int().nullable(),
    status: z.enum(SUBJECT_REQUEST_STATUSES),
    mode: z.enum(ERASE_MODES).nullable(),
    preview: eraseOutcomeSchema.nullable(),
    result: eraseOutcomeSchema.nullable(),
    /** Ön izleme ile yürütme sayıları aynı mı (yalnız tamamlanmış silme talebinde) */
    previewMatched: z.boolean().nullable(),
    errorCode: z.string().nullable(),
    fileSize: z.number().int().nullable(),
    requestedBy: userRefSchema,
    createdAt: z.string(),
    finishedAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
});
export const subjectRequestListSchema = z.object({ data: z.array(subjectRequestSchema) });
const requestKeySchema = z.string().regex(/^[A-Za-z0-9._:-]{8,100}$/);
export const subjectExportCreateSchema = z
    .object({
    recordType: z.enum(SUBJECT_RECORD_TYPES),
    recordId: z.guid(),
    /** İstemcinin ürettiği tekillik anahtarı: aynı anahtar ikinci talep üretmez */
    requestKey: requestKeySchema,
})
    .strict();
export const subjectEraseCreateSchema = z
    .object({
    recordType: z.enum(SUBJECT_RECORD_TYPES),
    recordId: z.guid(),
    mode: z.enum(ERASE_MODES),
    requestKey: requestKeySchema,
})
    .strict();
/** Yürütme onayında yazılması gereken metin (kipe göre) */
export const ERASE_CONFIRM_TEXT = {
    delete: 'KALICI SİL',
    anonymize: 'ANONİMLEŞTİR',
};
/** Yeniden kimlik doğrulama: parola + (iki adımlı doğrulama açıksa) kod */
export const reauthSchema = z.object({
    password: z.string().min(1).max(200),
    totpCode: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
});
export const subjectEraseConfirmSchema = reauthSchema
    .extend({ confirmText: z.string().min(1).max(40) })
    .strict();
export const exportDownloadTokenSchema = z.object({ token: z.string(), expiresAt: z.string() });
/** Dışa aktarma paketinin saklama süresi (saat) ve ön izlemenin onay için geçerlilik süresi (saat) */
export const SUBJECT_EXPORT_TTL_HOURS = 72;
export const SUBJECT_PREVIEW_TTL_HOURS = 24;
/** Paket içine alınan dosyaların toplam üst sınırı (bayt); aşan dosyalar listede kalır, içerik eklenmez */
export const SUBJECT_EXPORT_FILES_MAX_BYTES = 100 * 1024 * 1024;
export const SUBJECT_EXPORT_FORMAT_VERSION = 1;
// ---------------------------------------------------------------------------------------------------------------------
// Hesap kapatma
// ---------------------------------------------------------------------------------------------------------------------
/** Dondurma süresi (gün): plan §8 varsayılanı 30; taban veritabanındaki tetikleyiciyle aynı (7) */
export const CLOSURE_FREEZE_DAYS = { default: 30, min: 7, max: 90 };
export const TENANT_STATUSES = ['active', 'suspended', 'closing', 'closed'];
/**
 * Çalışma alanı durum makinesi (kapatma akışı). `owner`: sahibin uygulama içi işlemleri; `operator`: operatör aracı;
 * `system`: süre dolunca çalışan kalıcı silme işi (`purged` = satır artık yok, iz silme defterindedir).
 */
export function canTenantTransition(from, to, actor) {
    if (from === 'active' && to === 'closing')
        return actor === 'owner' || actor === 'operator';
    if (from === 'closing' && to === 'active')
        return actor === 'owner' || actor === 'operator';
    if (from === 'closing' && to === 'purged')
        return actor === 'system';
    if (from === 'active' && to === 'suspended')
        return actor === 'operator';
    if (from === 'suspended' && to === 'active')
        return actor === 'operator';
    return false;
}
export const closureStatusSchema = z.object({
    status: z.enum(TENANT_STATUSES),
    closingAt: z.string().nullable(),
    purgeAfter: z.string().nullable(),
    freezeDays: z.number().int(),
    /** Onay için yazılması gereken metin (çalışma alanı kısa adı) */
    confirmText: z.string(),
});
export const closureRequestSchema = reauthSchema
    .extend({ confirmText: z.string().min(1).max(60) })
    .strict();
export const closureCancelSchema = reauthSchema
    .extend({
    tenantSlug: z.string().min(3).max(40),
    email: z.email().max(254),
})
    .strict();
// ---------------------------------------------------------------------------------------------------------------------
// Denetim izi görüntüleme
// ---------------------------------------------------------------------------------------------------------------------
export const AUDIT_PAGE_MAX = 100;
export const AUDIT_EXPORT_MAX_ROWS = 10_000;
/** Denetim izi saklama tabanı (ay): veritabanı tetikleyicisi daha yeni satırın silinmesine izin vermez */
export const AUDIT_RETENTION_FLOOR_MONTHS = 12;
const isoDate = z.iso.datetime({ offset: true });
export const auditQuerySchema = z
    .object({
    from: isoDate.optional(),
    to: isoDate.optional(),
    /** Eylem adı ya da ön eki (`record.` → `record.` ile başlayanlar) */
    action: z
        .string()
        .regex(/^[a-z][a-z0-9_.-]{0,79}$/)
        .optional(),
    actorUserId: z.guid().optional(),
    /** Kayıt kimliği (kayıt bağı ya da varlık kimliği) */
    recordId: z.guid().optional(),
    /** Yalnız destek erişimi oturumlarının eylemleri */
    supportOnly: z.enum(['true', 'false']).optional(),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(AUDIT_PAGE_MAX).default(50),
})
    .strict();
export const auditEventSchema = z.object({
    id: z.string(),
    createdAt: z.string(),
    action: z.string(),
    entityType: z.string(),
    entityId: z.string().nullable(),
    recordId: z.string().nullable(),
    actor: userRefSchema,
    /** F4: asistan eylemi (kullanıcısız; profil kimliği + güncel görünen ad). İnsan/sistem eyleminde `null`. */
    assistant: assistantActorSchema.default(null),
    requestId: z.string().nullable(),
    /** Destek erişimi oturumunda yapılan eylem */
    support: z.boolean(),
    /** Hassas alanları maskelenmiş ayrıntı */
    data: z.unknown(),
});
export const auditListSchema = z.object({
    data: z.array(auditEventSchema),
    nextCursor: z.string().nullable(),
    retentionMonths: z.number().int(),
});
const SECRET_KEY_RE = /(pass|parola|sifre|şifre|secret|token|api[_-]?key|authorization|cookie|envelope|hash|otp)/i;
const PERSONAL_KEY_RE = /(mail|posta|phone|telefon|gsm|address|adres|iban|tckn|identity|kimlik|tax_?no|vergi|first_?name|last_?name|full_?name|^name$|^ad$|^soyad$|^to$|^cc$|recipient|alici|alıcı|requester|^ip$|_ip$|ipaddress)/i;
/**
 * Serbest metin belirteçleri, öncelik sırasıyla: (1) uuid, (2) ISO tarih / tarih-saat — ikisi de KORUNUR; (3) e-posta,
 * (4) rakam dizisi (telefon / kimlik numarası adayı). Sıra önemlidir: uuid ve tarih önce eşleşmezse içlerindeki rakam
 * dizileri telefon sanılıp bozulurdu.
 */
const TEXT_TOKEN_RE = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})|(\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?)|([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})|(\+?\d[\d\s().-]{7,}\d)/gi;
/** Bu kadar ve daha çok rakam taşıyan dizi telefon / kimlik numarası adayıdır; daha kısası (tutar, sayaç) korunur */
const LONG_NUMBER_MIN_DIGITS = 10;
export const MASK = '•••';
function maskEmail(value) {
    const [local, domain] = value.split('@');
    if (local === undefined || domain === undefined)
        return MASK;
    const dot = domain.lastIndexOf('.');
    const tld = dot >= 0 ? domain.slice(dot) : '';
    return `${local.slice(0, 1)}${MASK}@${domain.slice(0, 1)}${MASK}${tld}`;
}
/** Serbest metindeki e-posta ve telefon / uzun numara kalıplarını maskeler; uuid, tarih ve kısa sayılar korunur */
export function maskText(value) {
    return value.replace(TEXT_TOKEN_RE, (m, uuid, date, email) => {
        if (uuid !== undefined || date !== undefined)
            return m;
        if (email !== undefined)
            return maskEmail(m);
        const digits = m.replace(/\D/g, '');
        return digits.length >= LONG_NUMBER_MIN_DIGITS ? `${MASK}${digits.slice(-2)}` : m;
    });
}
/**
 * Denetim izi ayrıntısının gösterim maskesi. Sır taşıyabilecek anahtarların değeri tümüyle gizlenir; kişisel veri
 * taşıyabilecek anahtarların değeri kısaltılır; diğer metinlerde e-posta/telefon kalıpları maskelenir. Kimlik (uuid), sayı,
 * tarih ve durum değerleri korunur. Derinlik ve boyut sınırlıdır (aşan kısım atılır).
 */
export function maskAuditData(value, key = '', depth = 0) {
    if (value === null || value === undefined)
        return null;
    if (depth > 6)
        return MASK;
    if (SECRET_KEY_RE.test(key))
        return MASK;
    if (typeof value === 'string') {
        if (PERSONAL_KEY_RE.test(key)) {
            if (value.includes('@'))
                return maskText(value);
            return value.length <= 1 ? MASK : `${value.slice(0, 1)}${MASK}`;
        }
        return maskText(value.length > 500 ? `${value.slice(0, 500)}…` : value);
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return PERSONAL_KEY_RE.test(key) && typeof value === 'number' ? MASK : value;
    }
    if (Array.isArray(value))
        return value.slice(0, 50).map((v) => maskAuditData(v, key, depth + 1));
    if (typeof value === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(value).slice(0, 100))
            out[k] = maskAuditData(v, k, depth + 1);
        return out;
    }
    return MASK;
}
// ---------------------------------------------------------------------------------------------------------------------
// Destek erişimi
// ---------------------------------------------------------------------------------------------------------------------
export const SUPPORT_ACCESS_MAX_HOURS = 24;
export const SUPPORT_ACCESS_SCOPES = ['read_only'];
export const SUPPORT_ACCESS_SCOPE_LABELS = {
    read_only: 'Salt okunur',
};
/** Destek oturumu belirtecinin taşındığı başlık (çerez ve Bearer'dan ayrı; tarayıcı kendiliğinden göndermez) */
export const SUPPORT_TOKEN_HEADER = 'x-support-token';
/** Denetim izinde destek oturumu isteklerinin eylem adı */
export const SUPPORT_AUDIT_ACTION = 'support.access.request';
export const supportGrantCreateSchema = z
    .object({
    hours: z.number().int().min(1).max(SUPPORT_ACCESS_MAX_HOURS),
    reason: z.string().trim().min(3).max(300),
    scope: z.enum(SUPPORT_ACCESS_SCOPES).default('read_only'),
})
    .strict();
export const SUPPORT_GRANT_STATUSES = ['pending', 'active', 'expired', 'revoked'];
export const SUPPORT_GRANT_STATUS_LABELS = {
    pending: 'Kod bekliyor',
    active: 'Oturum açık',
    expired: 'Süresi doldu',
    revoked: 'İptal edildi',
};
/** İznin güncel durumu: iptal > süre > kod kullanıldı mı */
export function supportGrantStatus(grant, now) {
    if (grant.revokedAt !== null)
        return 'revoked';
    if (new Date(grant.expiresAt).getTime() <= now.getTime())
        return 'expired';
    return grant.redeemedAt === null ? 'pending' : 'active';
}
export const supportGrantSchema = z.object({
    id: z.guid(),
    scope: z.enum(SUPPORT_ACCESS_SCOPES),
    reason: z.string(),
    status: z.enum(SUPPORT_GRANT_STATUSES),
    createdAt: z.string(),
    expiresAt: z.string(),
    redeemedAt: z.string().nullable(),
    revokedAt: z.string().nullable(),
    grantedBy: userRefSchema,
    session: z
        .object({
        operator: z.string(),
        startedAt: z.string(),
        lastSeenAt: z.string().nullable(),
        requestCount: z.number().int(),
    })
        .nullable(),
});
export const supportGrantListSchema = z.object({ data: z.array(supportGrantSchema) });
/** Oluşturma yanıtı: tek kullanımlık kod YALNIZ burada döner */
export const supportGrantCreatedSchema = z.object({ grant: supportGrantSchema, code: z.string() });
/** Operatör etiketi (kimlik doğrulaması değildir; iz içindir) */
export const SUPPORT_OPERATOR_RE = /^[A-Za-z0-9][A-Za-z0-9._@-]{1,59}$/;
// ---------------------------------------------------------------------------------------------------------------------
// Silme defteri (yönetim görünümü + dışa aktarma biçimi)
// ---------------------------------------------------------------------------------------------------------------------
export const LEDGER_SCOPES = ['subject_erase', 'record_purge', 'tenant_purge'];
export const LEDGER_SCOPE_LABELS = {
    subject_erase: 'Veri öznesi silme',
    record_purge: 'Saklama süresi temizliği',
    tenant_purge: 'Çalışma alanı kapatma',
};
/** Dışa aktarılan defter satırı (JSON satırı). Kişisel veri içermez. */
export const ledgerEntrySchema = z.object({
    entryId: z.guid(),
    tenantId: z.guid(),
    scope: z.enum(LEDGER_SCOPES),
    recordType: z.string().nullable(),
    recordId: z.guid().nullable(),
    mode: z.enum(ERASE_MODES).nullable(),
    counts: z.record(z.string(), z.unknown()),
    actorUserId: z.guid().nullable(),
    requestId: z.guid().nullable(),
    executedAt: z.string(),
    voidedAt: z.string().nullable(),
    voidedBy: z.string().nullable(),
    voidNote: z.string().nullable(),
});
export const ledgerViewSchema = z.object({
    data: z.array(z.object({
        entryId: z.guid(),
        scope: z.enum(LEDGER_SCOPES),
        recordType: z.string().nullable(),
        recordId: z.guid().nullable(),
        mode: z.enum(ERASE_MODES).nullable(),
        executedAt: z.string(),
        replayCount: z.number().int(),
    })),
});
//# sourceMappingURL=data-lifecycle.js.map